import { Anchor, Button, Fieldset, Group, Spoiler, Text, Typography } from "@mantine/core";
import { IconChevronDown, IconChevronUp, IconClockCancel, IconListDetails } from "@tabler/icons-react";
import { zodResolver } from "mantine-form-zod-resolver";
import { StrictUndef } from "shared/api/httpClient";
import { DetailCard } from "shared/components/global/DetailCard";
import {
    ComposeFormProvider,
    composeFormStateSchema,
    createComposeFormState,
    useComposeForm,
} from "shared/components/message/compose/context";
import { ComposeForm } from "shared/components/message/ComposeForm";
import { useMessageActions } from "shared/components/message/useMessageActions";
import { usePromise } from "shared/hooks/usePromise";
import { useAside } from "shared/utils/aside";
import { mdToHtml } from "shared/utils/md";
import { zDate } from "shared/utils/zod";
import { ComposeDetailsFragment } from "src/gql/graphql";

type ScheduledCardProps = {
    message: StrictUndef<ComposeDetailsFragment>;
};

export const ScheduledCard = ({ message }: ScheduledCardProps) => {
    const __html = usePromise(() => mdToHtml(message.richBody), [message.richBody]);
    const actions = useMessageActions();
    const aside = useAside();

    function handleViewMessage(message: StrictUndef<ComposeDetailsFragment>) {
        const Panel = () => {
            const form = useComposeForm({
                initialValues: { ...createComposeFormState(message), meta: { mode: "read" } },
                validate: zodResolver(composeFormStateSchema),
            });

            return (
                <ComposeFormProvider form={form}>
                    <Fieldset variant="unstyled" disabled>
                        <ComposeForm />
                    </Fieldset>
                </ComposeFormProvider>
            );
        };

        aside.present({
            label: "Scheduled Message",
            component: <Panel />,
            mode: "drawer",
        });
    }

    return (
        <DetailCard
            key={message.id}
            title={message.subject}
            subtitle={
                <Text size="sm">
                    Scheduled for&nbsp;
                    <Anchor onClick={() => void actions.openScheduleDeliveryModal({ getMessage: () => Promise.resolve(message) })}>
                        {zDate.parse(message.scheduledFor).toLocaleString()}
                    </Anchor>
                </Text>
            }
            rightSection={
                <Button
                    size="compact-sm"
                    variant="default"
                    leftSection={<IconClockCancel size={16} />}
                    onClick={() => void actions.cancelScheduledDelivery(message.id)}
                >
                    Cancel
                </Button>
            }
            actions={[{ label: "View Message", onClick: () => handleViewMessage(message), icon: <IconListDetails size={16} /> }]}
        >
            <Spoiler
                maxHeight={80}
                styles={{ control: { width: "100%" } }}
                showLabel={
                    <>
                        <Group w="100%" justify="center" align="center" gap="2px">
                            <Button size="compact-xs" variant="transparent" component="a" rightSection={<IconChevronDown size={16} />}>
                                More
                            </Button>
                        </Group>
                    </>
                }
                hideLabel={
                    <Group w="100%" justify="center" align="center" gap="2px">
                        <Button size="compact-xs" variant="transparent" component="a" rightSection={<IconChevronUp size={16} />}>
                            Less
                        </Button>
                    </Group>
                }
            >
                <Typography>
                    <span dangerouslySetInnerHTML={{ __html }} />
                </Typography>
            </Spoiler>
        </DetailCard>
    );
};
